import AvatarContainer from '../common/AvatarContainer';
import RIHeader from './RIHeader';
import RIInfo from './RIInfo';
import './RequestItem.scss';
import { prefixClass } from 'lib/utils';
import { parseAR } from './utils/parseAR';
import { AskRequest } from '@hone-automation/common';
import { RequestItemProps } from './type';
import classNames from 'classnames';
import { IconBlockerAlert, IconChevron, IconMentionUnfill } from 'components/Icons';
import React, { useEffect, useState, useMemo } from 'react';
import { convertFromShortcodes } from '../utils/emojiParser';
import DOMPurify from 'dompurify';
import { mentionUtils } from '../CMComposer/mentionUtils';
import { useGetAllUsersByLocation } from 'hooks';
import { useQueryState } from 'hooks/useQueryState';
import { useParams } from 'react-router-dom';
import { ProfileEntryParams } from 'presentation/components/NewTable/types';
import { extractUsersFromComments, mergeActiveAndCommentUsers } from '../utils/userUtils';

export type RequestItemParams = {
  read: boolean;
  multiLocation: boolean;
  selected?: boolean;
  colorId?: string;
  request: AskRequest;
  me: boolean;
  blocker?: boolean;
  onClick?: (id: string) => void;
  compact?: boolean;
};

const RequestItem: React.FC<RequestItemParams> = ({
  read = false,
  multiLocation = false,
  colorId,
  selected = false,
  request,
  me = false,
  blocker = false,
  onClick,
  compact = false,
}) => {
  // Exit before any hook and rendering if request is not defined
  if (!request) return null;

  const prefix = prefixClass('request-item');
  const [requestId] = useQueryState<string | null>('requestId');
  const { locationId: routeLocationId } = useParams<ProfileEntryParams>();

  const { data: usersFetched } = useGetAllUsersByLocation(routeLocationId);
  const requestItem: RequestItemProps = parseAR(request);
  const { id, userInfo, header, location, info, lastComment, warnning } = requestItem;

  const [isSelected, setIsSelected] = useState(selected);

  // Check if ALL comments have 'internal' visibility property
  // Using a type-safe approach with optional chaining and checking for property existence
  const hasInternalComments = useMemo(() => {
    if (!request.comments || request.comments.length === 0) return false;
    // Return true only if ALL comments have visibility set to 'internal' or 'Internal'
    return request.comments.every(comment => {
      // Using any to access potential visibility property without TypeScript errors
      const commentAny = comment as any;
      return commentAny.visibility === 'internal' || commentAny.visibility === 'Internal';
    });
  }, [request.comments]);

  // Extract all unique users from comments (including deactivated ones and tagged users)
  const allUsersFromComments = useMemo(() => extractUsersFromComments(request.comments), [request.comments]);

  // Merge active users with users from comments to include deactivated users
  const allUsers = useMemo(
    () => mergeActiveAndCommentUsers(usersFetched, allUsersFromComments),
    [usersFetched, allUsersFromComments]
  );

  useEffect(() => {
    if (selected) {
      setIsSelected(true);
      return;
    }
    setIsSelected((requestId as string)?.trim() === request.id?.trim());
  }, [requestId, selected]);

  const StatusIndicator = () => {
    return (
      <div className={prefix('status-avatar-container-activity')}>
        {blocker && (
          <div className={prefix('status-avatar-container-blocker')}>
            <IconBlockerAlert />
          </div>
        )}
        {!read && (
          <div className={classNames(prefix('status-avatar-container-activity-status'), { warning: warnning })}></div>
        )}
      </div>
    );
  };

  const LastComment = () => {
    if (!lastComment) return null;
    const purifyConfig = {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li', 'p', 'br', 'strike', 'del', 'div'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
      ADD_ATTR: ['target', 'rel'],
      KEEP_CONTENT: true,
    };
    const hasMentions = lastComment?.comment.match(/::[A-Za-z0-9]+::/g);
    const sanitizedContent = convertFromShortcodes(DOMPurify.sanitize(lastComment.comment, purifyConfig));
    const sanitizedContentWithMentions = mentionUtils.formatMentionsToHtml(sanitizedContent, allUsers);

    const parsedContent = { __html: sanitizedContentWithMentions };
    return (
      <div className={classNames(prefix('last-comment'), { 'has-mentions': hasMentions })}>
        {hasMentions && <IconMentionUnfill />}
        <div dangerouslySetInnerHTML={parsedContent} />
      </div>
    );
  };

  const handleClick = () => {
    if (onClick) {
      onClick(id);
    }
  };
  if (!userInfo || !header) return null;

  return (
    <div
      className={classNames(prefix(), { 'request-selected': isSelected, 'request-blocker': blocker })}
      onClick={handleClick}
      data-request-id={id} // Add data attribute for scrolling functionality
    >
      <div className={prefix('status-avatar-container')}>
        <AvatarContainer
          userInfo={{ ...userInfo, colorId: colorId || userInfo.colorId }}
          tooltip={true}
          me={me}
          isHoneInternalUser={hasInternalComments || userInfo.isHoneInternalUser}
        />
        <StatusIndicator />
      </div>
      <div className={classNames(prefix('body-container'), { 'no-last-comment': !lastComment })}>
        <RIHeader
          userName={header.userName}
          date={header.date}
          locationName={location.name}
          locationId={location.id}
          isMultiLocation={multiLocation}
          isInternal={hasInternalComments}
          isKSUser={userInfo.isHoneInternalUser}
          title={userInfo.title}
          compact={compact}
        />
        <RIInfo {...info} />

        <LastComment />
      </div>
      <div className={prefix('selected-arrow-container')}>
        <div className={prefix('selected-arrow-container-icon')}>
          <IconChevron />
        </div>
      </div>
    </div>
  );
};

export default RequestItem;
